import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, CircularProgress, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

import { Card } from '../../sections/@dashboard/find-edifier';
import { getUserDetails, sortByLname } from '../../utils';
import { useQueryGetUsersList } from '../../hooks/react-query/useData';
import { UserDetailedInformationDialog } from '../../components/dialog';
import ViewCoachDialog from '../../components/dialog/ViewCoachDialog';
import CustomPagination from '../../components/pagination/CustomPagination';
import { SelectRole } from '../../components/filters';

export default function PrincipalUsers() {
  const userDetails = getUserDetails();
  const userToken = userDetails.token;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [displayUserDetails, setDisplayUserDetails] = useState(false);
  const [currentUser, setCurrentUser] = useState({});

  const [viewDialogData, setViewDialogData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search state
  const [searchedValue, setSearchedValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state
  const [currentSelectedFilter, setCurrentSelectedFilter] = useState('All');

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search functions
  const searchFromRecord = () => {
    setSearchQuery(searchedValue);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchedValue('');
    setSearchQuery('');
    setPage(0);
  };

  // Fetch users list - automatically filtered by principal's assigned schools on backend
  const { data: usersList, isFetching: isLoading } = useQueryGetUsersList(
    {
      token: userToken,
      page: page + 1,
      limit: rowsPerPage,
      ...(searchQuery && { search: searchQuery }),
      ...(currentSelectedFilter !== 'All' && {
        userRole: currentSelectedFilter === 'Edifiers' ? 'COACH' :
          currentSelectedFilter === 'Teachers' ? 'TEACHER' :
            currentSelectedFilter === 'Site Leaders' ? 'SITE-LEADER' : null
      }),
    },
    navigate,
    toast
  );


  // Users list from API (already filtered by principal's assigned schools)
  const usersData = usersList?.data?.data?.users || [];
  const sortedUsers = sortByLname(usersData);

  const userCard = (index, list) => (
    <Card
      key={index}
      data={list}
      index={index}
      setDisplayUserDetails={setDisplayUserDetails}
      setCurrentUser={setCurrentUser}
      userRole={userDetails.role.toLowerCase()}
      setEdifierToEditData={setViewDialogData}
      setIsView={(value) => value && setIsViewDialogOpen(true)}
      setOpen={() => { }}
      setItemToDeleteId={() => { }}
      setEditEdifierOpen={() => { }}
      setChangeImage={() => { }}
      setIdToChangeImage={() => { }}
    />
  );

  console.log(currentSelectedFilter);
  

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Users</title>
      </Helmet>

      <UserDetailedInformationDialog
        open={displayUserDetails}
        handleClose={() => setDisplayUserDetails(false)}
        userData={currentUser}
      />

      {isViewDialogOpen && <ViewCoachDialog
        isView={isViewDialogOpen}
        handleClose={() => {
          setIsViewDialogOpen(false);
          setViewDialogData(null);
        }}
        edifierToEditData={viewDialogData}
      />}

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
            Users
          </Typography>
        </Box>

        {/* Search and Filter Section */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex',
            columnGap: '17px',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
          }}>
            <TextField
              placeholder="Search Users by Name"
              className="filters"
              value={searchedValue}
              sx={{ minWidth: '233px', maxWidth: { xs: '100%', md: '233px' } }}
              onChange={(e) => setSearchedValue(e.target.value.toLowerCase())}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchFromRecord();
                }
              }}
            />

            <Button
              variant="contained"
              color="main"
              sx={{ height: 'fit-content', my: 'auto', width: 'max-content' }}
              onClick={() => searchFromRecord()}
            >
              Search
            </Button>

            {(searchQuery || searchedValue) && (
              <Button
                variant="outlined"
                color="secondary"
                sx={{ height: 'fit-content', my: 'auto', width: 'max-content' }}
                onClick={clearSearch}
              >
                Clear
              </Button>
            )}

            <SelectRole
              allRoles={[
                { _id: 0, name: 'All' },
                { _id: 1, name: 'Teachers' },
                { _id: 2, name: 'Edifiers' },
                { _id: 3, name: 'Site Leaders' }
              ]}
              currentSelectedFilter={currentSelectedFilter}
              setCurrentSelectedFilter={setCurrentSelectedFilter}
            />
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ textAlign: 'center', mt: '30px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {sortedUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: '30px' }}>
                <Typography variant="h6" color="text.secondary">
                  No users found
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={'28.47px'} sx={{ mt: '20px' }}>
                {sortedUsers.map((list, index) => userCard(index, list))}
              </Grid>
            )}

            {usersList?.data?.data?.pagination && (
              <CustomPagination
                count={usersList.data.data.pagination.totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

